import {Injectable, Signal} from '@angular/core';
import {HttpClient, HttpParams, httpResource} from '@angular/common/http';
import {Observable} from 'rxjs';
import {
  Accessory, AddEquipmentAccessoryRequest,
  AddEquipmentVariantAccessoryRequest, EquipmentAccessory,
  EquipmentVariantAccessory,
  PageResponse
} from '../interface/accessories';
import {environment} from '../../environments/environment';
import {withActiveLocation} from "../context/withActivelocation";
import {LocationList} from "../feature/settings/location/interface/location";

interface AddStockPayload {
  location: string;
  reasonType?: number;
  quantity: number;
}

interface LedgerResponse {
  id: number;
  quantity: number;
  reason: {
    id: number;
    reason: string;
  };
  location: {
    id: string;
    name: string;
    latitude: number;
    longitude: number;
    active: boolean;
    defaultLocation: boolean;
    createdAt: string;
    updatedAt: string;
    locationType: {
      id: number;
      code: string;
      name: string;
      description: string;
    };
  };
  createdAt?: string;
  updatedAt?: string;
  equipmentVariant: string;
}

@Injectable({
  providedIn: 'root'
})


export class AccessoryService {


  constructor(private http: HttpClient) {
  }

  getAccessories(
    page: Signal<number>,
    size: Signal<number>,
    query: Signal<string | undefined>,
    equipmentId: Signal<string | undefined>
  ) {
    return httpResource<PageResponse<Accessory>>(() => {
      const currentPage = page();
      const currentSize = size();
      const currentQuery = query();
      const currentEquipmentId = equipmentId();

      let params = new HttpParams()
        .set('page', currentPage)
        .set('size', currentSize)
        .set('sort', 'createdAt,DESC');

      if (currentQuery) {
        params = params.set('query', currentQuery);
      }

      if (currentEquipmentId) {
        params = params.set('equipmentId', currentEquipmentId);
      }

      return {
        method: 'GET',
        url: `${environment.basePath}/api/consumables`,
        params
      };
    });
  }

  getEquipmentVariantAccessoryV2(equipmentVariantId: Signal<string | null>, activeLocationId: Signal<LocationList | null>) {
    return httpResource<EquipmentVariantAccessory[]>(() => {
      const activeLocation = activeLocationId()?.id;
      const id = equipmentVariantId();
      if (!id || !activeLocation) return;

      return {
        method: 'GET',
        url: `${environment.basePath}/api/equipment-variant/${id}/consumables`,
        ...withActiveLocation()
      };
    });
  }

  // Create accessory
  createAccessory(payload: {
    name: string;
    categoryId: string;
    unit: string;
  }): Observable<Accessory> {
    return this.http.post<Accessory>(`${environment.basePath}/api/consumables`, payload);
  }

  // Update accessory
  updateAccessory(
    consumableId: number,
    payload: {
      name: string;
      categoryId: string;
      unit: string;
    }
  ): Observable<Accessory> {
    return this.http.put<Accessory>(`${environment.basePath}/api/consumables/${consumableId}`, payload);
  }

  // Get accessory by ID
  getAccessoryById(consumableId: number): Observable<Accessory> {
    return this.http.get<Accessory>(`${environment.basePath}/api/consumables/${consumableId}`);
  }

  getAccessoryLedgers(consumableId: number, reasonId?: number): Observable<LedgerResponse[]> {
    return this.http.get<LedgerResponse[]>(
      `${environment.basePath}/api/consumables/${consumableId}/ledgers`,
      {
        ...withActiveLocation(),
        params: reasonId ? { reasonId: reasonId } : {}
      }
    );
  }

  addStockToLocation(consumableId: number, payload: AddStockPayload): Observable<any> {
    return this.http.put<any>(
      `${environment.basePath}/api/consumables/${consumableId}/bulk-ledger/in`,
      payload
    );
  }

  addEquipmentVariantAccessory(equipmentVariantId: string, payload: AddEquipmentVariantAccessoryRequest): Observable<void> {
    return this.http.post<void>(`${environment.basePath}/api/equipment-variant/${equipmentVariantId}/consumables`,
      payload)
  }

  getEquipmentAccessory(inventoryItemId: string): Observable<EquipmentAccessory[]> {
    return this.http.get<EquipmentAccessory[]>(`${environment.basePath}/api/inventory-unit/${inventoryItemId}/accessories`);
  }

  addEquipmentAccessory(inventoryItemId: string, payload: AddEquipmentAccessoryRequest): Observable<EquipmentAccessory> {
    return this.http.post<EquipmentAccessory>(`${environment.basePath}/api/inventory-unit/${inventoryItemId}/accessory`,
      payload)
  }

  updateEquipmentAccessory(inventoryItemId: string,accessoryId:number, payload: AddEquipmentAccessoryRequest): Observable<EquipmentAccessory> {
    return this.http.put<EquipmentAccessory>(`${environment.basePath}/api/inventory-unit/${inventoryItemId}/accessories/${accessoryId}`,
      payload)
  }

  getEquipmentAccessoryById(inventoryItemId:string,accessoryId: number): Observable<EquipmentAccessory> {
    return this.http.get<EquipmentAccessory>(`${environment.basePath}/api/inventory-unit/${inventoryItemId}/accessories/${accessoryId}`);
  }

  deleteEquipmentAccessory(
    inventoryItemId: string,
    accessoryId: number
  ): Observable<string> {

    return this.http.delete(
      `${environment.basePath}/api/inventory-unit/${inventoryItemId}/accessories/${accessoryId}`,
      { responseType: 'text' }
    );
  }

  getConsumableDetail(consumableId: number){
    return this.http.get(`${environment.basePath}/api/consumables/${consumableId}/summary`);
  }

}
